import { useId } from "react";
import { Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectNoneItem,
  SelectTrigger,
  SelectValue,
  clearable,
} from "@/components/ui/select";

interface FieldString {
  value: string;
  onChange: (value: string) => void;
}

export interface ListFilterBarProps {
  /**
   * Heading shown in the colored top bar of the panel — e.g.
   * `"Search Quotation"`, `"Search Customer"`. The pattern is
   * `Search <Module>` so the panel reads identically across the app.
   */
  title: string;
  /**
   * The free-text search input. Anchors the TOP of the vertical panel on
   * every list module so users hit the same place across the app.
   */
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Document number contains-match (quote no., waybill no., invoice no., etc.). */
  docNo?: FieldString & { placeholder?: string };
  /**
   * Customer free-text filter. The list of suggestions is supplied via
   * `options` and rendered behind a native <datalist> so the input stays
   * searchable without pulling in a new combobox component.
   */
  customer?: FieldString & {
    options: string[];
    placeholder?: string;
  };
  /**
   * Status select. Always include an "All" option in `options` — the parent
   * is responsible for the sentinel value (typically "all").
   */
  status?: FieldString & {
    options: Array<{ value: string; label: string }>;
    placeholder?: string;
  };
  /** From date (inclusive, ISO yyyy-mm-dd). */
  dateFrom?: FieldString;
  /** To date (inclusive, ISO yyyy-mm-dd). */
  dateTo?: FieldString;
  /** Whether any filter dimension is currently active. Controls "Clear" visibility. */
  hasAnyFilter: boolean;
  /** Reset every filter in one go. */
  onClear: () => void;
  /** Extra classes for the panel wrapper (e.g. sticky positioning). */
  className?: string;
}

/**
 * Vertical "Search <Module>" panel used by every list module in the app.
 * Renders a coloured header bar over a stacked column of inputs:
 *   - Free-text search
 *   - Optional document-number input
 *   - Optional customer datalist input
 *   - Optional status select
 *   - Optional From/To date pair on a single row, 50/50 split
 *   - "Clear filters" button at the bottom, only when something is set
 *
 * The component intentionally stays presentational — URL persistence and
 * filter logic live in `useFilterParam` / the parent page so this panel
 * can be swapped or restyled without touching state plumbing. Each
 * consumer is responsible for placing the panel in a 2-column grid
 * alongside its table.
 */
export default function ListFilterBar({
  title,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  docNo,
  customer,
  status,
  dateFrom,
  dateTo,
  hasAnyFilter,
  onClear,
  className,
}: ListFilterBarProps) {
  const customerDatalistId = useId();
  const showDateRow = Boolean(dateFrom || dateTo);
  return (
    <div
      className={`overflow-hidden rounded-md border border-border bg-amber-50/40 dark:bg-amber-950/10 shadow-card ${className ?? ""}`}
    >
      <div className="border-b border-border bg-amber-100 px-3 py-2 dark:bg-amber-900/30">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-100">
          {title}
        </h2>
      </div>
      <div className="space-y-2 p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="h-9 w-full bg-background pl-9"
            aria-label="Search"
          />
        </div>
        {docNo ? (
          <Input
            placeholder={docNo.placeholder ?? "Document #"}
            value={docNo.value}
            onChange={(e) => docNo.onChange(e.target.value)}
            className="h-9 w-full bg-background"
            aria-label="Filter by document number"
          />
        ) : null}
        {customer ? (
          <>
            <Input
              list={customerDatalistId}
              placeholder={customer.placeholder ?? "Customer"}
              value={customer.value}
              onChange={(e) => customer.onChange(e.target.value)}
              className="h-9 w-full bg-background"
              aria-label="Filter by customer"
            />
            <datalist id={customerDatalistId}>
              {customer.options.map((opt) => (
                <option key={opt} value={opt} />
              ))}
            </datalist>
          </>
        ) : null}
        {status ? (
          <Select value={status.value} onValueChange={clearable(status.onChange)}>
            <SelectTrigger
              className="h-9 w-full bg-background"
              aria-label="Filter by status"
            >
              <SelectValue placeholder={status.placeholder ?? "Status"} />
            </SelectTrigger>
            <SelectContent>
              <SelectNoneItem />
              {status.options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        {showDateRow ? (
          <div className="grid grid-cols-2 gap-2">
            {dateFrom ? (
              <div className="space-y-1">
                <span className="block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  From Date
                </span>
                <Input
                  type="date"
                  value={dateFrom.value}
                  onChange={(e) => dateFrom.onChange(e.target.value)}
                  onClick={(e) => {
                    const el = e.currentTarget as HTMLInputElement & {
                      showPicker?: () => void;
                    };
                    el.showPicker?.();
                  }}
                  className="h-9 w-full cursor-pointer bg-background [color-scheme:light] dark:[color-scheme:dark]"
                  aria-label="From date"
                  max={dateTo?.value || undefined}
                />
              </div>
            ) : (
              <div />
            )}
            {dateTo ? (
              <div className="space-y-1">
                <span className="block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  To Date
                </span>
                <Input
                  type="date"
                  value={dateTo.value}
                  onChange={(e) => dateTo.onChange(e.target.value)}
                  onClick={(e) => {
                    const el = e.currentTarget as HTMLInputElement & {
                      showPicker?: () => void;
                    };
                    el.showPicker?.();
                  }}
                  className="h-9 w-full cursor-pointer bg-background [color-scheme:light] dark:[color-scheme:dark]"
                  aria-label="To date"
                  min={dateFrom?.value || undefined}
                />
              </div>
            ) : (
              <div />
            )}
          </div>
        ) : null}
        {hasAnyFilter ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClear}
            className="mt-1 w-full"
          >
            <X className="mr-1 h-4 w-4" />
            Clear filters
          </Button>
        ) : null}
      </div>
    </div>
  );
}
